import React from 'react'
import { Avatar, Box, Button, Paper, Typography, CircularProgress, Link } from '@mui/material'
import StatusChip from './StatusChip'
import { capitalize } from '../utils/string'
import { Partida } from '../services/partidaService'
import { atletasDoLado, atletaEstaNoLado, ladoVencedor } from '../utils/atletasDoLado'
import LocalizacaoMapaView from './local/LocalizacaoMapaView'

export interface PartidaItemCategoriaResumo {
  categoriaTorneioId: string
  torneioId: string
  torneio: {
    nome: string
    avatarUrl?: string | null
    dataInicio?: string | null
    dataFim?: string | null
  }
  categoria: {
    nome: string
    numeroAtletas: number
  }
  equipesInscritas: number
  partidasAtletaCount: number
  proximaFaseNome: string
  partidas?: Partida[]
}

export interface PartidaItemProps {
  atletaId: string
  partida?: Partida
  categoriaResumo?: PartidaItemCategoriaResumo
  onVerInscricao: () => void
  verInscricaoLoading?: boolean
  /** Callback para abrir a trilha do atleta */
  onShowTrilha?: () => void
  /** Callback para navegação para a página de partidas da categoria */
  onNavigateToCategory?: () => void
}

/**
 * Determina o status da partida para um atleta específico
 * @param partida - Objeto da partida
 * @param atletaId - ID do atleta
 * @returns 'vitoria' | 'derrota' | 'pendente' | 'empate'
 */
const getPartidaStatus = (partida: Partida, atletaId: string): 'vitoria' | 'derrota' | 'pendente' | 'empate' => {
  // Verificar se a partida está finalizada
  if (partida.status !== 'FINALIZADA') {
    return 'pendente'
  }

  // Identificar o lado do atleta (equipe ou participante avulso — rodízio)
  const isEquipeA = atletaEstaNoLado(partida, 'A', atletaId)
  const isEquipeB = atletaEstaNoLado(partida, 'B', atletaId)

  // Se não estiver em nenhum lado, considerar pendente
  if (!isEquipeA && !isEquipeB) {
    return 'pendente'
  }

  // Usar o lado vencedor ("A"/"B") para determinar o resultado — cobre rodízio
  // (equipeA/B nulos) e legado (a fonte única normaliza ambos).
  const ladoV = ladoVencedor(partida)
  if (ladoV) {
    return ladoV === (isEquipeA ? 'A' : 'B') ? 'vitoria' : 'derrota'
  }

  // Se não houver equipeVencedora, verificar se há placar para determinar empate
  if (partida.placar?.resultadoFinalA != null && partida.placar?.resultadoFinalB != null) {
    const resA = partida.placar.resultadoFinalA
    const resB = partida.placar.resultadoFinalB
    if (resA === resB) {
      return 'empate'
    }
  }

  return 'pendente'
}

/** Item de listagem de partidas */
const PartidaItem: React.FC<PartidaItemProps> = ({
  atletaId,
  partida,
  categoriaResumo,
  onVerInscricao,
  verInscricaoLoading,
  onShowTrilha,
  onNavigateToCategory,
}) => {
  const nomeTorneio = categoriaResumo
    ? categoriaResumo.torneio.nome
    : (partida?.categoriaTorneio?.torneio?.nome ?? '')
  const avatarTorneio = categoriaResumo
    ? (categoriaResumo.torneio.avatarUrl ?? '/assets/images/avatar-torneio.png')
    : (partida?.categoriaTorneio?.torneio?.avatarUrl ?? '/assets/images/avatar-torneio.png')
  const nomeCategoria = categoriaResumo
    ? categoriaResumo.categoria.nome
    : (partida?.categoriaTorneio?.nome ?? '')
  // let statusTorneio = '';
  // const dataFimTorneio = partida.categoriaTorneio?.torneio?.dataFim ? new Date(partida.categoriaTorneio.torneio.dataFim) : undefined;
  // const dataInicioTorneio = partida.categoriaTorneio?.torneio?.dataInicio ? new Date(partida.categoriaTorneio.torneio.dataInicio) : undefined;
  const now = new Date();
  // const proximaPartida = partida.categoriaTorneio?.partidas?.filter(partida => new Date(partida.data) > now && partida.status !== 'CANCELADA')[0];
  let statusPartida = '';
  if (categoriaResumo) {
    const di = categoriaResumo.torneio.dataInicio
    statusPartida = di ? `Data: ${new Date(di).toLocaleDateString('pt-BR')}` : ''
  } else if (partida) {
    statusPartida = 'Torneio em andamento'
    if (partida.status === 'FINALIZADA' || new Date(partida.data) < now) {
      statusPartida = 'Terminou em ' + new Date(partida.data).toLocaleDateString('pt-BR');
    }
  }

  // if (dataFimTorneio && dataFimTorneio < now) {
  //   statusTorneio = `Terminou em ${dataFimTorneio.toLocaleDateString('pt-BR')}`;
  // } else if (dataInicioTorneio && dataInicioTorneio > now) {
  //   statusTorneio = `Inicia em ${dataInicioTorneio.toLocaleDateString('pt-BR')}`;
  // } else {
  //   statusTorneio = 'Torneio em andamento';
  // }

  let nomeAtletaDupla = '';
  if (!categoriaResumo && partida) {
    const lado: 'A' | 'B' = atletaEstaNoLado(partida, 'A', atletaId) ? 'A' : 'B';
    const atletasLado = atletasDoLado(partida, lado);
    if (partida.categoriaTorneio?.numeroAtletas === 2 && atletasLado.length > 0) {
      const atletaDupla = atletasLado.find(a => a.id !== atletaId) || null;
      nomeAtletaDupla = atletaDupla?.nome || '';
    }
  }

  const equipesInscritas = categoriaResumo ? categoriaResumo.equipesInscritas : 0;
  const numJogos = categoriaResumo ? categoriaResumo.partidasAtletaCount : 0;

  const nomeFase = categoriaResumo ? categoriaResumo.proximaFaseNome : (partida?.faseCategoriaTorneio?.nome || '');

  const resultadoPartida: string[] = (() => {
    if (!partida) return []
    const sets = partida?.placar?.sets || []
    const res: string[] = Array.from({ length: sets.length ?? 0 }).map(() => 'grey')
    for (let i = 0; i < res.length; i++) {
      if (sets[i] && sets[i].gamesA != null && sets[i].gamesB != null) {
        if (Number(sets[i].gamesA) > Number(sets[i].gamesB)) {
          res[i] = 'success.main'
        } else if (Number(sets[i].gamesA) < Number(sets[i].gamesB)) {
          res[i] = 'red'
        }
      }
    }
    return res
  })()



  const handleAvatarClick = () => {
    if (onNavigateToCategory) {
      onNavigateToCategory()
    }
  }

  const handleNomeTorneioClick = () => {
    if (onNavigateToCategory) {
      onNavigateToCategory()
    }
  }

  return (
    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', mb: 0, borderRadius: 0 }} >
      {/* Avatar do torneio */}
      {onNavigateToCategory ? (
        <Avatar 
          src={avatarTorneio} 
          sx={{ 
            width: 48, 
            height: 48, 
            mr: 2, 
            boxShadow: 1,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            }
          }}
          onClick={handleAvatarClick}
        />
      ) : (
        <Avatar src={avatarTorneio} sx={{ width: 48, height: 48, mr: 2, boxShadow: 1 }} />
      )}

      {/* Informacoes principais */}
      <Box sx={{ flexGrow: 1, maxWidth: '40%' }}>
        {/* Nome do torneio */}
        {onNavigateToCategory ? (
          <Typography 
            variant="subtitle1" 
            fontWeight="bold"
            onClick={handleNomeTorneioClick}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.main',
                textDecoration: 'underline',
              }
            }}
          >
            {nomeTorneio} - <span className='text-dark fs-5'>{nomeCategoria}</span>
          </Typography>
        ) : (
          <Typography variant="subtitle1" fontWeight="bold">
            {nomeTorneio} - <span className='text-dark fs-5'>{nomeCategoria}</span>
          </Typography>
        )}
        {/* Data do torneio */}
        <Typography variant="caption" color="text.secondary" display="block" className='fs-4'>
          {statusPartida}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {/* Status da partida */}
          {!categoriaResumo && partida && (
            <div className='d-flex align-items-center gap-1 bg-light px-2 py-1 rounded'>
              {capitalize(partida.status)}
            </div>
          )}
          {nomeAtletaDupla && (
            <div className='d-flex align-items-center gap-1 bg-light px-2 py-1 rounded'>
              <i className="bi bi-person-fill" />
              {nomeAtletaDupla}
            </div>
          )}
        </Box>
        {!categoriaResumo && partida && (
          <Box sx={{ mt: 1 }}>
            <LocalizacaoMapaView urlLocalizacao={partida.local?.urlLocalizacao} />
          </Box>
        )}
      </Box>

      {/* Quantidade de equipes inscritas e jogos*/}
      <Box sx={{ flexGrow: 1 }}>
        <div className='row'>
          <div className='col'>
            <Typography variant="body2" color="text.primary" fontWeight="bold" fontSize={'2rem'} align='right'>
              {equipesInscritas} 
            </Typography>
            <Typography variant="body2" color="text.secondary" align='right'>
              Equipes
            </Typography>
          </div>
          <div className='col'>
            <Typography variant="body2" color="text.primary" fontWeight="bold" fontSize={'2rem'} align='left'>
              {numJogos}
            </Typography>
            <Typography variant="body2" color="text.secondary" align='left'>
              Jogos
            </Typography>
          </div>
        </div>
      </Box>

      {/* Indicadores */}
      <Box sx={{ mr: 2, textAlign: 'right' , flexGrow: 1, maxWidth: '20%' }}>
        {/* Botao Ver inscricao */}
        <Button
          variant="outlined"
          size="medium"
          className='mb-2 text-primary border-primary'
          onClick={onVerInscricao}
          disabled={verInscricaoLoading}
          sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
        >
          {verInscricaoLoading ? (
            <CircularProgress size={16} />
          ) : (
            'Ver inscricao'
          )}
        </Button>
        {categoriaResumo && categoriaResumo.partidas && categoriaResumo.partidas.length > 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', mt: 1 }}>
            {/* Bullets de partidas à esquerda */}
            <Box sx={{ display: 'flex', gap: 0.5, paddingRight: '10px' }}>
              {categoriaResumo.partidas.map((p) => {
                const status = getPartidaStatus(p, atletaId)
                const color = status === 'vitoria' ? '#28a745' 
                  : status === 'derrota' ? '#dc3545' 
                  : status === 'empate' ? '#ffc107' 
                  : '#6c757d'
                return (
                  <Box
                    key={p.id}
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: color,
                    }}
                  />
                )
              })}
            </Box>
            {/* Trilha à direita */}
            <Typography fontSize={'1em'}>
              {nomeFase} - {' '}
              <Link
                style={{ textDecoration: 'none', cursor: 'pointer' }}
                onClick={onShowTrilha}
              >
                Trilha
              </Link>
            </Typography>
          </Box>
        ) : (
          <>
            <Typography fontSize={'1em'}>
              {nomeFase} - {' '}
              <Link
                style={{ textDecoration: 'none', cursor: 'pointer' }}
                onClick={onShowTrilha}
              >
                Trilha
              </Link>
            </Typography>
            {!categoriaResumo && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                {/* Quadrados verdes para sets vencidos */}
                {Array.from({ length: resultadoPartida.length ?? 0 }).map((_, i) => (
                  <Box key={`v-${i}`} sx={{ width: 16, height: 16, bgcolor: `${resultadoPartida[i]}`, mr: 0.5 }} />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      
    </Paper>
  )
}

export default PartidaItem
